import { RequestType } from '../dataaccess/requests/types';
import { ActionTypes } from '../dataaccess/actions/types';

export const getDecisions = (requestType?: RequestType) => {
  switch (requestType) {
    case RequestType.BRANDING:
      return brandDecisions;
    case RequestType.NEWS:
      return newsDecisions;
    default:
      return [
        ...brandDecisions,
        ...newsDecisions,
        sendTmlReminderDecision,
        sendArtworkReminderDecision,
        approveArtworkDecision,
        declineArtworkDecision,
        forwardArtworkDecision,
      ];
  }
};

export enum LicenseType {
  TML = 'tml',
  PLA = 'pla',
}

export interface Decision {
  type: DecisionType;
  label: DecisionLabel;
  action: Actions;
  actionType: ActionTypes;
  title?: DecisionTitle;
  subtitle?: DecisionSubtitle;
  description?: DecisionDescription;
  answerString?: DecisionAnswerString;
  helpText?: DecisionHelpString;
  license?: Licenses;
  link?: string;
  subject?: string;
}

export enum DecisionType {
  APPROVE_TML = 'approve_tml',
  APPROVE = 'approve',
  NO_VISUAL = 'no_visual',
  DECLINE = 'decline',
  FORWARD = 'forward',
  SEND_EMAIL = 'send_email',
  REDIRECT = 'redirect',
  INACTIVE = 'inactive',
  APPROVE_PLA = 'approve_pla',
  SEND_TML_REMINDER = 'send_tml_reminder',
  SEND_ARTWORK_REMINDER = 'send_artwork_reminder',
  APPROVE_ARTWORK = 'approve_artwork',
  DECLINE_ARTWORK = 'decline_artwork',
  FORWARD_ARTWORK = 'forward_artwork',
}

export interface SelectFieldOption {
  value: any;
  label: string;
  disabled?: boolean;
  selected?: boolean;
}

enum DecisionLabel {
  APPROVE_TML = 'Approve with TML',
  APPROVE = 'Approve no TML',
  APPROVE_PLA = 'Approve with Licence Agreement',
  NO_VISUAL = 'No visual, send design',
  DECLINE = 'Decline with message',
  FORWARD = 'Forward as email',
  SEND_EMAIL = 'Send email',
  REDIRECT = 'Redirect as email',
  INACTIVE = 'Set as inactive',
  SEND_TML_REMINDER = 'TML Reminder',
  SEND_ARTWORK_REMINDER = 'A/W Reminder',
  APPROVE_ARTWORK = 'Approve final a/w',
  DECLINE_ARTWORK = 'Decline final a/w',
  FORWARD_ARTWORK = 'Forward final a/w',
}

enum DecisionTitle {
  APPROVE_TML = 'Approve with TML',
  APPROVE_PLA = 'Approval for using BBC assets with a Production Licence Agreement',
  APPROVE = 'Approve no TML',
  NO_VISUAL = 'Send assets if no visual supplied',
  DECLINE = 'Decline request with message',
  FORWARD = 'Forward request details as an email',
  SEND_EMAIL = 'Send email to requester',
  REDIRECT = 'Redirect request details as an email',
  INACTIVE = 'Set as inactive',
  SEND_TML_REMINDER = 'In process reminder to accept terms of the Trade Mark Licence',
  SEND_ARTWORK_REMINDER = 'In process reminder to upload final artwork',
  APPROVE_ARTWORK = 'Approve final artwork',
  DECLINE_ARTWORK = 'Decline final artwork',
  FORWARD_ARTWORK = 'Forward final artwork for approval',
}

enum DecisionSubtitle {
  APPROVE_TML = 'Approve request from {{CONTACT_NAME}} of {{COMPANY_NAME}} with TML',
  APPROVE_PLA = 'Approve request from {{COMPANY_NAME}} with Production Licence Agreement',
  APPROVE = 'Approve request from {{COMPANY_NAME}} with no TML',
  NO_VISUAL = '',
  DECLINE = 'Decline request from {{COMPANY_NAME}}',
  FORWARD = 'Forward details of request from {{CONTACT_NAME}} of {{COMPANY_NAME}} received on {{DATE}}',
  SEND_EMAIL = 'Send email to {{CONTACT_NAME}} of {{COMPANY_NAME}}',
  REDIRECT = 'Forward details of request from {{CONTACT_NAME}} of {{COMPANY_NAME}} received on {{DATE}}',
  INACTIVE = 'Set as inactive',
  SEND_TML_REMINDER = 'Reminder for request from {{COMPANY_NAME}}',
  SEND_ARTWORK_REMINDER = 'Reminder for request from {{COMPANY_NAME}}',
  APPROVE_ARTWORK = 'Approve final artwork from {{COMPANY_NAME}}',
  DECLINE_ARTWORK = 'Decline final artwork from {{COMPANY_NAME}}',
  FORWARD_ARTWORK = 'Forward final artwork(s) from {{CONTACT_NAME}} of {{COMPANY_NAME}}',
}

enum DecisionDescription {
  APPROVE_TML = 'This template will send an email to the requester granting use of BBC assets if the TML is agreed. Additional text can be added if required.',
  APPROVE_PLA = "This page will send an email to the requester granting use of BBC assets if the Production Licence Agreement is agreed. Fill in your name and the date below and click the 'send email' button.",
  APPROVE = 'This template will send an email to the requester granting permission to use BBC assets. Additional text can be added if required.',
  NO_VISUAL = 'This page sends a link to the requester to download the BBC logos that have been approved for use prior to being used in designs to be sent for approval.',
  DECLINE = 'This template sends an email to the requester declining the request to use BBC assets with an extra specific message relating to the request.',
  FORWARD = '',
  SEND_EMAIL = '',
  REDIRECT = '',
  INACTIVE = '',
  SEND_TML_REMINDER = "This template will send an email to the requester reminding them that they haven't agreed to the terms of the Trade Mark Licence.",
  SEND_ARTWORK_REMINDER = 'This template will send an email to the requester reminding them to upload their final artwork.',
  APPROVE_ARTWORK = 'This sends a message to the requester approving the uploaded final artwork.',
  DECLINE_ARTWORK = 'This sends a message to the requester declining the approval of the final artwork. Specific reasons should be entered in the box.',
}

enum DecisionAnswerString {
  APPROVE_TML = 'Your BBC brand request has been approved subject to a Trade Mark licence and BBC approval of your final artwork(s).',
  APPROVE_PLA = 'Your BBC brand request has been approved subject to your acceptance of the Production Licence Agreement.',
  APPROVE = 'Many thanks for your request.<br/> This email grants you approval for use of the BBC asset, in the way you have outlined for the time frame requested, subject to BBC approval of the final artwork.<br><br/>Please download the requested BBC asset(s) and their respective guidelines by using the links below. Links will expire in 7 days.</br>',
  NO_VISUAL = 'You have not supplied a design visual with your request. We cannot consider your request until we see how BBC assets will be used. Please download your requested logo(s) and guidelines from the link(s) below, add the logo to your design visual and upload for approval. </br> </br>A Trade Mark Licence will only be issued once the design visual has been uploaded and approved.<br/>Your requested logo(s) and guidelines. These links will expire in 36 hours:',
  SEND_TML_REMINDER = 'Your BBC brand request has been approved.<br/>Please click on the link below to accept the terms and conditions of the Trade Mark licence.',
  SEND_ARTWORK_REMINDER = 'Your BBC brand request is subject to approval of your final artwork(s).<br/>Please click on the link below to upload your final artwork. The link will expire in 7 days.',
  APPROVE_ARTWORK = 'Thank you for uploading your final artwork(s).<br/>These are now approved for the time frame requested and subject to the Trade Mark licence terms and conditions.',
  DECLINE_ARTWORK = 'Thank you for uploading your final artwork(s).<br/>This is not approved for the following reasons: (add in reason on case by case basis)',
}

// decision's second part of the message, ususally guidance text for the user.
enum DecisionHelpString {
  APPROVE_TML = 'Please click on the link below to accept the terms and conditions of the Trade Mark licence.',
  APPROVE_PLA = 'Please click on the link below to accept the terms and conditions of the Production Licence Agreement.',
  APPROVE = 'The BBC must approve your final artwork that uses the logo(s) requested. Please upload the artwork as a jpg, pdf or Word doc, via the link below.',
  NO_VISUAL = 'Follow the link below to upload your Final Artwork for approval:',
}

// temporary with string instead of eg: payload
enum Actions {
  APPROVE_TML = 'navigating to send email with TML link',
  APPROVE_PLA = 'navigating to send email with PLA link',
  APPROVE = 'navigating to send email w/o TML',
  NO_VISUAL = 'navigating to send email: send assets',
  DECLINE = 'navigating to send email with decline reasons',
  FORWARD = 'navigating to send email with details and other data',
  REDIRECT = 'navigating to send email same as FORWARD but with select "category"',
  SEND_EMAIL = 'navigating to send email basic',
  INACTIVE = 'set inactive',
  SEND_TML_REMINDER = 'tml reminder',
  SEND_ARTWORK_REMINDER = 'final artwork reminder',
  APPROVE_ARTWORK = 'Approve artwork',
  DECLINE_ARTWORK = 'Decline artwork',
  FORWARD_ARTWORK = 'Forward artwork',
  SEND_LINK = 'send an upload link to the client',
  SEND_LINK_WITH_ASSET = 'send an upload link to the client + asset download link',
}

export enum Licenses {
  TML = `<p>The BBC grants to the Licensee (“you”) a non-exclusive licence to use the trade marks described in section 2 above (“the Trade Mark(s)”) on the following conditions:</p><ol><li>The Trade Mark(s) may be used only on the item(s) described in section 3, which must be used only for the purpose set out in section 4 and distributed only in the Territory stated in section 6 (or, if an internet page, directed at that Territory).</li><li>The use will be only as shown on the sample(s) of the item(s) attached to the copy of this licence, for which purpose you will provide a sample of each item on which you propose to use the Trade Marks (in PDF or other format acceptable to the BBC) to the BBC, with a request for approval of that item. Until the BBC has approved the use of the Trade Marks on each item, the use is deemed not approved.</li><li>The artwork for any logo(s) used in the item(s) as issued must be as supplied by the BBC.</li><li>You will use the Trade Mark(s) on the item(s) only during the period shown in section 5 above but you will stop using it earlier than the stated end date (1) if the Main Agreement (if there is one) is terminated or (2) if the BBC instructs you in writing to stop because you have not complied with the terms of this licence, or because you become insolvent, or because you are a company and you undergo a change of control, or because your use of the Trade Mark(s) infringes the rights of anyone else.</li><li>The manner in which the item(s) is/are used will comply with any relevant BBC guidelines notified to you, with any conditions in the Main Agreement (if there is one), and with any relevant laws, codes of practice, standards and regulations, will be in keeping with the editorial integrity and reputation of the BBC and the Trade Mark(s) and will not indicate an endorsement by the BBC.</li><li>If required by the BBC, you will ensure that the notice “[Name(s) of theTrade Mark(s)] are trade marks of the British Broadcasting Corporation and are used under licence” (or such shorter notice as the BBC approves) appears in whatever position on the item(s) the BBC reasonably requires.</li><li>You will send the BBC the item(s) on which the Trade Mark(s) appear immediately it has been issued (and/or, if applicable, a link to the website when the page is live).</li><li>You acknowledge that the BBC is the owner of the Trade Mark(s) and any copyright in it/them and of the goodwill relating to it/them, and that the benefit of the use of the Trade Mark(s) by you will belong to the BBC. You will not make or attempt to make any trade mark application in relation to the Trade Mark(s). The BBC gives no warranty that there are no conflicting third party rights in the Trade Mark(s) in any part of the Territory, and you will indemnify the BBC for any loss which it suffers as a result of your use of the Trade Mark(s) and, if required by the BBC, insure yourself against this liability.</li><li>You will not use the Trade Mark(s) in any way or for any purpose other than as set out in this licence, nor make use of the name of the BBC or any of its trade marks except under this or any other written licence granted to you by the BBC.</li><li>If you become aware of any infringement of the BBC’s rights in the Trade Mark(s) or any claim that your use of them infringes anyone else’s rights, you will inform the BBC’s Head of Corporate Legal and Intellectual Property in writing as soon as possible and do nothing further, other than to render to the BBC whatever assistance it reasonably requires in any action which it may take against the infringer or claimant, as the case may be.</li><li>You will not authorise any third party to use the Trade Mark(s) nor assign, transfer, sublicense or in any other way dispose of your rights under this licence.</li><li>This licence sets out the entire agreement between the BBC and you in relation to this matter and shall be interpreted in accordance with English law, and any dispute shall be subject to the jurisdiction of the English courts.</li></ol>`,
  PLA = `
    <ol>
      <li>
        This letter confirms that [DELETE "in consideration…this letter" IF NOT APPLICABLE] in consideration for payment of a £XXX [INSERT VALUE AS APPLICABLE] administration fee (payable within 14 days of the date of this letter) the BBC consents, subject to the terms set out in paragraphs 2 to 4 below, to your use of BBC Assets (as depicted and attached hereto as Schedule A [ADD FURTHER SCHEDULES IF/AS APPLICABLE]) and referred to as the 'BBC Assets' in the production '[INSERT PRODUCTION NAME]' (as described in Schedule B [OR RELEVANT LETTER]; the "Production" [PRODUCTION SYNOPSIS]) as set out in the attached script pages (attached hereto as Schedule C [OR RELEVANT LETTER]). [CHECK ALL DOCUMENTS AND SCHEDULES REFERRED TO IN THE AGREEMENT ARE NAMED AS INDIVIDUALLY LETTERED SCHEDULES AND THAT EACH LETTTERED SCHEDULE {A,B, C etc} CORRECTLY CORRESPONDS TO THOSE NAMED IN THE AGREEMENT FOR EACH PURPOSE].
      </li>
      <li>
        The purpose, manner and context of use of the BBC Assets will be as described in paragraph 1 above and in the attached script pages and for no other purpose nor in any other manner or context, and shall not in any way reflect derogatorily on the BBC nor detract from the high reputation which the BBC enjoys. The BBC confirms that the manner of use of the BBC Assets as proposed in Schedules [INSERT ALL SCHEDULE NAMES 'A', 'B', 'C' ETC AS APPLICABLE] is in compliance with this paragraph.
      </li>
      <li>
        The visual appearance of the BBC Assets must conform to the representation and files attached as Schedule A [INCLUDE ADDITIONAL SCHEDULE NAMES IF/AS APPLICABLE] and the context of how the BBC Assets as it will appear in the Production must be approved by the BBC (c/o BBC Brand Guardianship and BBC News Rights Management) prior to filming.</li><li>The BBC agrees that the Production, including the scenes featuring the BBC Assets may be reproduced and exploited in all media now known or hereafter devised, worldwide and in perpetuity, PROVIDED THAT:
      </li>
    </ol>
  
    <ul>
      <li>
        (i) there is no other reference to the BBC, nor any representation of any BBC personnel or characters (whether fictional or not), nor other BBC property in the Production, except as agreed separately in writing by the BBC (by way of examples only, BBC footage would, unless separately authorised by the BBC in writing, constitute a breach of this term);
      </li>
      <li>
        (ii) the Production will not bring the BBC into disrepute. (The BBC confirms that, having reviewed Schedules [LIST ALL SCHEDULE NAMES], it does not consider that the manner of use of the BBC Assets as proposed in these Schedules will bring the BBC into disrepute);
      </li>
      <li>
        (iii) clips or stills from the Production in which the BBC Assets appear will not be used or licensed by you for any ulterior purpose, including but not limited to advertising or promoting products other than the Production, without the prior written consent of the BBC. Notwithstanding the foregoing, you may use clips or stills from the Production in which the BBC Assets appear in connection with the production, exhibition, exploitation, advertising, and promotion of the Production.
      </li>
    </ul>  
`,
}

const approveTMLDecision = {
  type: DecisionType.APPROVE_TML,
  label: DecisionLabel.APPROVE_TML,
  action: Actions.APPROVE_TML,
  actionType: ActionTypes.ApproveWithTML,
  title: DecisionTitle.APPROVE_TML,
  subtitle: DecisionSubtitle.APPROVE_TML,
  description: DecisionDescription.APPROVE_TML,
  answerString: DecisionAnswerString.APPROVE_TML,
  helpText: DecisionHelpString.APPROVE_TML,
  license: Licenses.TML,
  link: '/accept/tml/{request_id}',
};

const approveNoTMLDecision = {
  type: DecisionType.APPROVE,
  label: DecisionLabel.APPROVE,
  action: Actions.APPROVE,
  actionType: ActionTypes.ApproveNoTML,
  title: DecisionTitle.APPROVE,
  subtitle: DecisionSubtitle.APPROVE,
  description: DecisionDescription.APPROVE,
  answerString: DecisionAnswerString.APPROVE,
  helpText: DecisionHelpString.APPROVE,
  link: '/fileupload/{request_id}/artworks',
};

const approvePLADecision = {
  type: DecisionType.APPROVE_PLA,
  label: DecisionLabel.APPROVE_PLA,
  action: Actions.APPROVE_PLA,
  actionType: ActionTypes.ApproveWithPLA,
  title: DecisionTitle.APPROVE_PLA,
  subtitle: DecisionSubtitle.APPROVE_PLA,
  description: DecisionDescription.APPROVE_PLA,
  answerString: DecisionAnswerString.APPROVE_PLA,
  helpText: DecisionHelpString.APPROVE_PLA,
  license: Licenses.PLA,
  link: '/accept/pla/{request_id}',
};

const noVisualDecision = {
  type: DecisionType.NO_VISUAL,
  label: DecisionLabel.NO_VISUAL,
  action: Actions.NO_VISUAL,
  actionType: ActionTypes.NoVisualSendDesign,
  title: DecisionTitle.NO_VISUAL,
  description: DecisionDescription.NO_VISUAL,
  answerString: DecisionAnswerString.NO_VISUAL,
  helpText: DecisionHelpString.NO_VISUAL,
  link: '/fileupload/{request_id}/files',
};

const declineDecision = {
  type: DecisionType.DECLINE,
  label: DecisionLabel.DECLINE,
  action: Actions.DECLINE,
  actionType: ActionTypes.DeclineWithMessage,
  title: DecisionTitle.DECLINE,
  subtitle: DecisionSubtitle.DECLINE,
  description: DecisionDescription.DECLINE,
};

export const sendEmailDecision = {
  type: DecisionType.SEND_EMAIL,
  label: DecisionLabel.SEND_EMAIL,
  action: Actions.SEND_EMAIL,
  actionType: ActionTypes.SendEmail,
  title: DecisionTitle.SEND_EMAIL,
  subtitle: DecisionSubtitle.SEND_EMAIL,
  description: DecisionDescription.SEND_EMAIL,
};

const forwardDecision = {
  type: DecisionType.FORWARD,
  label: DecisionLabel.FORWARD,
  action: Actions.FORWARD,
  actionType: ActionTypes.ForwardAsEmail,
  title: DecisionTitle.FORWARD,
  subtitle: DecisionSubtitle.FORWARD,
  description: DecisionDescription.FORWARD,
};

const redirectDecision = {
  type: DecisionType.REDIRECT,
  label: DecisionLabel.REDIRECT,
  action: Actions.REDIRECT,
  actionType: ActionTypes.RedirectAsEmail,
  title: DecisionTitle.REDIRECT,
  subtitle: DecisionSubtitle.REDIRECT,
  description: DecisionDescription.REDIRECT,
};

const inactiveDecision = {
  type: DecisionType.INACTIVE,
  label: DecisionLabel.INACTIVE,
  action: Actions.INACTIVE,
  actionType: ActionTypes.SetAsInactive,
};

export const sendTmlReminderDecision = {
  type: DecisionType.SEND_TML_REMINDER,
  actionType: ActionTypes.SendReminder,
  label: DecisionLabel.SEND_TML_REMINDER,
  action: Actions.SEND_TML_REMINDER,
  title: DecisionTitle.SEND_TML_REMINDER,
  subtitle: DecisionSubtitle.SEND_TML_REMINDER,
  description: DecisionDescription.SEND_TML_REMINDER,
  answerString: DecisionAnswerString.SEND_TML_REMINDER,
  link: '/accept/tml/{request_id}',
  subject: 'BBC Brand request reminder',
};

export const sendArtworkReminderDecision = {
  type: DecisionType.SEND_ARTWORK_REMINDER,
  actionType: ActionTypes.SendReminder,
  label: DecisionLabel.SEND_ARTWORK_REMINDER,
  action: Actions.SEND_ARTWORK_REMINDER,
  title: DecisionTitle.SEND_ARTWORK_REMINDER,
  subtitle: DecisionSubtitle.SEND_ARTWORK_REMINDER,
  description: DecisionDescription.SEND_ARTWORK_REMINDER,
  answerString: DecisionAnswerString.SEND_ARTWORK_REMINDER,
  link: '/fileupload/{request_id}/artworks',
  subject: 'BBC Brand request reminder',
};

export const approveArtworkDecision = {
  type: DecisionType.APPROVE_ARTWORK,
  actionType: ActionTypes.ApproveArtwork,
  label: DecisionLabel.APPROVE_ARTWORK,
  action: Actions.APPROVE_ARTWORK,
  title: DecisionTitle.APPROVE_ARTWORK,
  subtitle: DecisionSubtitle.APPROVE_ARTWORK,
  description: DecisionDescription.APPROVE_ARTWORK,
  answerString: DecisionAnswerString.APPROVE_ARTWORK,
  subject: 'Approval of Final Artwork',
};

export const declineArtworkDecision = {
  type: DecisionType.DECLINE_ARTWORK,
  actionType: ActionTypes.DeclineArtwork,
  label: DecisionLabel.DECLINE_ARTWORK,
  action: Actions.DECLINE_ARTWORK,
  title: DecisionTitle.DECLINE_ARTWORK,
  subtitle: DecisionSubtitle.DECLINE_ARTWORK,
  description: DecisionDescription.DECLINE_ARTWORK,
  answerString: DecisionAnswerString.DECLINE_ARTWORK,
  link: '/fileupload/{request_id}/artworks',
  subject: 'Decline approval of final artwork',
};

export const forwardArtworkDecision = {
  type: DecisionType.FORWARD_ARTWORK,
  actionType: ActionTypes.ForwardArtwork,
  label: DecisionLabel.FORWARD_ARTWORK,
  action: Actions.FORWARD_ARTWORK,
  title: DecisionTitle.FORWARD_ARTWORK,
  subtitle: DecisionSubtitle.FORWARD_ARTWORK,
  subject: '',
};

const brandDecisions: Decision[] = [
  approveTMLDecision,
  approveNoTMLDecision,
  noVisualDecision,
  declineDecision,
  sendEmailDecision,
  forwardDecision,
  redirectDecision,
  inactiveDecision,
];

const newsDecisions: Decision[] = [
  approvePLADecision,
  declineDecision,
  forwardDecision,
  sendEmailDecision,
  inactiveDecision,
];
